import type {
  WidgetAutocheck,
  WidgetFees,
} from "@redotech/redo-storefront-widget-common/widget";
import type { ShopifyProducts } from "@redotech/shopify-client/ajax";
import * as z from "zod";
import { ReturnsCoverageBuilderInitialValueSchema } from "./save-coverage-builder";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isCoverageBuilderPreviewAutocheck(
  value: unknown,
): value is WidgetAutocheck {
  return (
    isRecord(value) &&
    typeof value.displayIcon === "boolean" &&
    typeof value.storefrontAccessToken === "string" &&
    isRecord(value.cartToggle) &&
    isRecord(value.packageProtection) &&
    isRecord(value.finalSaleReturns)
  );
}

const CoverageBuilderPreviewAutocheckSchema = z.custom<WidgetAutocheck>(
  isCoverageBuilderPreviewAutocheck,
);

function isCoverageBuilderPreviewFees(value: unknown): value is WidgetFees {
  return (
    isRecord(value) &&
    typeof value.price === "string" &&
    typeof value.enabled === "boolean" &&
    typeof value.productId === "number" &&
    typeof value.variantId === "number"
  );
}

const CoverageBuilderPreviewFeesSchema = z.custom<WidgetFees>(
  isCoverageBuilderPreviewFees,
);

function isCoverageBuilderPreviewShopifyProducts(
  value: unknown,
): value is ShopifyProducts {
  return isRecord(value) && isRecord(value.mainShopifyProduct);
}

const CoverageBuilderPreviewShopifyProductsSchema = z.custom<ShopifyProducts>(
  isCoverageBuilderPreviewShopifyProducts,
);

/** Explicit read model the embedded coverage builder needs during onboarding. */
export const getCoverageBuilderContext = {
  name: "getCoverageBuilderContext" as const,
  input: z.object({}),
  output: z.object({
    initialValue: ReturnsCoverageBuilderInitialValueSchema,
    previewAutocheck: CoverageBuilderPreviewAutocheckSchema,
    /** Synthetic shop data the host pushes to the storefront preview, since a
     *  mid-onboarding store has no Redo fee product yet. */
    previewFees: CoverageBuilderPreviewFeesSchema,
    previewShopifyProducts: CoverageBuilderPreviewShopifyProductsSchema,
    isShopifyPlus: z.boolean(),
  }),
};
